import { ReactNode, useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import { isoDay, periodError } from '../dashboard-filters';

export interface Period {
    from: string;
    to: string;
}

interface Props {
    value: Period;
    onChange: (period: Period) => void;
    /// Rótulo em cima e altura dos selects do projeto, para ficar ao lado deles sem destoar.
    stacked?: boolean;
}

/// O dia local: toISOString é UTC e, à noite no Brasil, já seria amanhã.
const today = () => isoDay(new Date());

/// Dois campos com o calendário nativo: já é acessível e aceita digitação, sem biblioteca.
export const DateRangeFilter = ({ value, onChange, stacked = false }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    // O que está nos campos pode estar no meio da digitação; o painel só recebe o período quando vale.
    const [draft, setDraft] = useState(value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setDraft(value), [value.from, value.to]);

    const error = periodError(draft);
    const reversed = error === 'reversed';

    const update = (next: Period) => {
        setDraft(next);

        if (!periodError(next)) onChange(next);
    };

    const field = {
        // Faz o navegador desenhar o seletor nativo no tema certo.
        colorScheme: controller.darkMode ? 'dark' : 'light',
        minWidth: stacked ? 150 : 168,
        '& .MuiInputBase-input': { color: cc.textPrimary, fontSize: '0.85rem', py: 1 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: cc.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: cc.borderStrong },
        '& .Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
        ...(stacked && {
            '& .MuiOutlinedInput-root': { height: 43, background: cc.surfaceBg, borderRadius: '5px' },
        }),
    };

    const labelled = (label: string, input: ReactNode) =>
        stacked ? <LabelledSelect cc={cc} label={label}>{input}</LabelledSelect> : input;

    return (
        <Box sx={{ display: 'flex', gap: stacked ? 2 : 1.5, flexWrap: stacked ? 'nowrap' : 'wrap', flexShrink: 0, position: 'relative' }}>
            {labelled(t('crmDashboard.dataInicial'), (
                <TextField
                    type="date"
                    size="small"
                    label={stacked ? undefined : t('crmDashboard.dataInicial')}
                    value={draft.from}
                    error={reversed}
                    inputProps={{ max: draft.to || today(), 'aria-label': t('crmDashboard.dataInicial') }}
                    onChange={(e) => update({ ...draft, from: e.target.value })}
                    sx={field}
                />
            ))}

            {labelled(t('crmDashboard.dataFinal'), (
                <TextField
                    type="date"
                    size="small"
                    label={stacked ? undefined : t('crmDashboard.dataFinal')}
                    value={draft.to}
                    error={reversed}
                    // Nao ha lead amanha.
                    inputProps={{ min: draft.from, max: today(), 'aria-label': t('crmDashboard.dataFinal') }}
                    onChange={(e) => update({ ...draft, to: e.target.value })}
                    sx={field}
                />
            ))}

            {/* Fora do fluxo: empurrar a linha desalinharia os campos dos selects ao lado. */}
            {reversed && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ position: 'absolute', top: '100%', left: 0, mt: 0.25, whiteSpace: 'nowrap' }}
                >
                    {t('crmDashboard.periodoInvertido')}
                </Typography>
            )}
        </Box>
    );
};
